import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopyOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';

type AgentPoolIdCellProps = {
  id?: string | null;
  /** Optional prefix shown before the id (e.g. "Flow", "EventSlot"). */
  prefix?: string;
  emptyLabel?: string;
};

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

/**
 * Ops tables need full Mongo ids — truncated prefixes are ambiguous across shells/flows.
 */
export default function AgentPoolIdCell({ id, prefix, emptyLabel = '—' }: AgentPoolIdCellProps) {
  const t = useTranslations();
  const notification = useNotifications();

  if (!id) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  const copyLabel = t('common.copyId', undefined, 'Copy ID');

  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          wordBreak: 'break-all',
          lineHeight: 1.35,
        }}
      >
        {prefix ? `${prefix} ` : ''}
        {id}
      </Typography>
      <Tooltip title={copyLabel}>
        <IconButton
          size="small"
          aria-label={copyLabel}
          onClick={() =>
            void copyText(id)
              .then(() => notification.notifySuccess(t('common.copied', undefined, 'Copied')))
              .catch(() => notification.notifyError(t('common.copyFailed', undefined, 'Copy failed')))
          }
          sx={{ mt: -0.25 }}
        >
          <ContentCopyOutlined fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
